import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  InputAdornment,
  Alert,
  FormControlLabel,
  CircularProgress,
  Divider,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import KeyIcon from '@mui/icons-material/Key';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TuneIcon from '@mui/icons-material/Tune';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import axios from '../../utils/axios';
import { useNotify } from '../../components/NotifyProvider';
import PickingMsSettingsDialog from '../../components/fulfillment/PickingMsSettingsDialog';

const MP_WITH_CRONS = new Set(['wb', 'ozon', 'yandex']);

const AI_SUPPORTED_MP = new Set(['wb', 'ozon', 'yandex']);

export default function MpAccounts() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    marketplace: 'wb',
    name: '',
    token: '',
    client_id: '',
    business_id: '',
    ms_org_id: '',
    nds_rate: '7',
  });
  const [activeState, setActiveState] = useState({});
  const [syncEnabled, setSyncEnabled] = useState({});
  const [noLogisticsFrom, setNoLogisticsFrom] = useState({});
  const [ndsRateState, setNdsRateState] = useState({});
  const [aiEnabled, setAiEnabled] = useState({});
  const [hasAiToken, setHasAiToken] = useState({});
  const [aiDialog, setAiDialog] = useState({
    open: false,
    accountId: null,
    name: '',
    marketplace: '',
    hasToken: false,
    hasOzonAdvertSecret: false,
  });
  const [aiForm, setAiForm] = useState({
    ai_token: '',
    client_id: '',
    business_id: '',
    ozon_advert_client_id: '',
    ozon_advert_client_secret: '',
    ai_autonomy_level: 'propose',
    nds_rate: '0',
  });
  const [aiShowToken, setAiShowToken] = useState(false);
  const [aiShowOzonSecret, setAiShowOzonSecret] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiError, setAiError] = useState('');

  // Диалог «Автозагрузка»: поаккаунтные тумблеры cron-задач packer_analytics.
  const [cronDialog, setCronDialog] = useState({ open: false, accountId: null, name: '', marketplace: '' });
  const [cronList, setCronList] = useState([]); // [{cron_name, label, enabled}, ...]
  const [cronLoading, setCronLoading] = useState(false);
  const [cronSaving, setCronSaving] = useState(false);
  const [cronError, setCronError] = useState('');
  const [pickingDialog, setPickingDialog] = useState({ open: false, accountId: null, name: '', marketplace: '' });
  const notify = useNotify();

  const load = async () => {
    try {
      const { data } = await axios.get('/ms/accounts');
      setRows(data || []);
      const a = {};
      (data || []).forEach((r) => {
        a[r.id] = r.active ?? true;
      });
      setActiveState(a);
      const s = {};
      (data || []).forEach((r) => {
        s[r.id] = r.sync_orders_enabled ?? true;
      });
      setSyncEnabled(s);
      const nl = {};
      const ai = {};
      const aiTok = {};
      const nds = {};
      (data || []).forEach((r) => {
        nl[r.id] = r.no_logistics_from || '';
        ai[r.id] = r.ai_enabled ?? false;
        aiTok[r.id] = r.has_ai_token ?? false;
        nds[r.id] = r.nds_rate != null ? String(Number(r.nds_rate)) : '0';
      });
      setNdsRateState(nds);
      setNoLogisticsFrom(nl);
      setAiEnabled(ai);
      setHasAiToken(aiTok);
    } catch (err) {
      console.error('Ошибка загрузки аккаунтов:', err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!form.name || !form.token) return;
    if (form.marketplace === 'ozon' && !form.client_id) return;
    if (form.marketplace === 'yandex' && (!form.client_id || !form.business_id)) return;
    await axios.post('/ms/accounts', form);
    setForm({ marketplace: 'wb', name: '', token: '', client_id: '', business_id: '', ms_org_id: '', nds_rate: '7' });
    await load();
  };

  const del = async (id) => {
    await axios.delete(`/ms/accounts/${id}`);
    await load();
  };

  // Inline-сохранение ставки НДС из таблицы (по blur/Enter).
  const saveNdsRate = async (id, raw) => {
    const s = String(raw ?? '').trim().replace(',', '.');
    const n = s === '' ? 0 : Number(s);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      notify.error('Ставка НДС должна быть числом от 0 до 100');
      return;
    }
    try {
      await axios.put(`/ms/accounts/${id}`, { nds_rate: n });
      setNdsRateState((prev) => ({ ...prev, [id]: String(n) }));
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, nds_rate: n } : r)));
      notify.success('Ставка НДС сохранена');
    } catch (err) {
      notify.error(err?.response?.data?.message || 'Не удалось сохранить ставку НДС');
    }
  };

  const openAiDialog = (row) => {
    setAiDialog({
      open: true,
      accountId: row.id,
      name: row.name,
      marketplace: row.marketplace,
      hasToken: !!row.has_ai_token,
      hasOzonAdvertSecret: !!row.has_ozon_advert_secret,
    });
    setAiForm({
      ai_token: '',
      client_id: row.client_id || '',
      business_id: row.business_id != null ? String(row.business_id) : '',
      ozon_advert_client_id: row.ozon_advert_client_id || '',
      ozon_advert_client_secret: '',
      ai_autonomy_level: row.ai_autonomy_level || 'propose',
      nds_rate: row.nds_rate != null ? String(Number(row.nds_rate)) : '0',
    });
    setAiShowToken(false);
    setAiShowOzonSecret(false);
    setAiError('');
  };

  const closeAiDialog = () => {
    setAiDialog({ open: false, accountId: null, name: '', marketplace: '', hasToken: false, hasOzonAdvertSecret: false });
    setAiForm({ ai_token: '', client_id: '', business_id: '', ozon_advert_client_id: '', ozon_advert_client_secret: '', ai_autonomy_level: 'propose', nds_rate: '0' });
    setAiShowToken(false);
    setAiShowOzonSecret(false);
    setAiError('');
  };

  const saveAiDialog = async () => {
    const { accountId, marketplace, hasToken } = aiDialog;
    if (!accountId) return;
    const tokenInput = aiForm.ai_token.trim();
    const clientId = aiForm.client_id.trim();
    const businessId = aiForm.business_id.trim();
    const ozonAdvertClientId = aiForm.ozon_advert_client_id.trim();
    const ozonAdvertClientSecret = aiForm.ozon_advert_client_secret.trim();

    if (marketplace === 'ozon' && !clientId) {
      setAiError('Для Ozon обязательно укажите Client-Id (api-seller.ozon.ru).');
      return;
    }
    if (marketplace === 'yandex') {
      if (!businessId) {
        setAiError('Для Yandex обязательно укажите businessId.');
        return;
      }
      if (!/^\d+$/.test(businessId)) {
        setAiError('businessId должен быть числом.');
        return;
      }
    }

    const willHaveToken = tokenInput.length > 0 || hasToken;
    if (aiEnabled[accountId] && !willHaveToken) {
      setAiError('Включён AI, но AI-токен не задан.');
      return;
    }

    const payload = {};
    if (tokenInput) payload.ai_token = tokenInput;
    if (clientId !== '' || marketplace !== 'wb') payload.client_id = clientId || null;
    if (businessId !== '' || marketplace === 'yandex') payload.business_id = businessId || null;
    if (marketplace === 'ozon') {
      payload.ozon_advert_client_id = ozonAdvertClientId || null;
      if (ozonAdvertClientSecret) {
        payload.ozon_advert_client_secret = ozonAdvertClientSecret;
      } else if (!ozonAdvertClientId) {
        // Очистка обоих полей: если client_id пуст и секрет тоже пуст —
        // намеренно сбрасываем секрет, чтобы не остался "висеть".
        payload.ozon_advert_client_secret = null;
      }
    }
    // Этап 6: уровень автономии для аккаунта.
    if (aiForm.ai_autonomy_level) {
      payload.ai_autonomy_level = aiForm.ai_autonomy_level;
    }
    // Ставка НДС (в процентах). Пустое поле трактуем как 0.
    {
      const raw = String(aiForm.nds_rate ?? '').trim().replace(',', '.');
      const nds = raw === '' ? 0 : Number(raw);
      if (!Number.isFinite(nds) || nds < 0 || nds > 100) {
        setAiError('Ставка НДС должна быть числом от 0 до 100.');
        return;
      }
      payload.nds_rate = nds;
    }

    setAiSaving(true);
    setAiError('');
    try {
      await axios.put(`/ms/accounts/${accountId}`, payload);
      await load();
      closeAiDialog();
    } catch (err) {
      setAiError(err?.response?.data?.message || err.message || 'Ошибка сохранения');
    } finally {
      setAiSaving(false);
    }
  };

  const openCronDialog = async (row) => {
    setCronDialog({ open: true, accountId: row.id, name: row.name, marketplace: row.marketplace });
    setCronList([]);
    setCronError('');
    setCronLoading(true);
    try {
      const { data } = await axios.get(`/mp-accounts/${row.id}/cron-settings`);
      setCronList(Array.isArray(data?.settings) ? data.settings : []);
    } catch (err) {
      setCronError(err?.response?.data?.message || err.message || 'Не удалось загрузить настройки');
    } finally {
      setCronLoading(false);
    }
  };

  const closeCronDialog = () => {
    if (cronSaving) return;
    setCronDialog({ open: false, accountId: null, name: '', marketplace: '' });
    setCronList([]);
    setCronError('');
  };

  const setAllCrons = (enabled) => {
    setCronList((prev) => prev.map((item) => ({ ...item, enabled })));
  };

  const toggleCron = (cronName, enabled) => {
    setCronList((prev) => prev.map((item) => (item.cron_name === cronName ? { ...item, enabled } : item)));
  };

  const saveCronDialog = async () => {
    const { accountId } = cronDialog;
    if (!accountId) return;
    setCronSaving(true);
    setCronError('');
    try {
      const payload = {
        settings: cronList.map(({ cron_name, enabled }) => ({ cron_name, enabled: !!enabled })),
      };
      await axios.post(`/mp-accounts/${accountId}/cron-settings`, payload);
      notify.success('Настройки автозагрузки сохранены');
      setCronDialog({ open: false, accountId: null, name: '', marketplace: '' });
      setCronList([]);
    } catch (err) {
      setCronError(err?.response?.data?.message || err.message || 'Ошибка сохранения');
    } finally {
      setCronSaving(false);
    }
  };

  const openPickingDialog = (row) => {
    setPickingDialog({ open: true, accountId: row.id, name: row.name, marketplace: row.marketplace });
  };

  const closePickingDialog = () => {
    setPickingDialog({ open: false, accountId: null, name: '', marketplace: '' });
  };

  const deleteAiToken = async () => {
    const { accountId } = aiDialog;
    if (!accountId) return;
    setAiSaving(true);
    try {
      await axios.post('/ms/accounts/ai-token', { account_id: accountId, ai_token: null });
      setHasAiToken((prev) => ({ ...prev, [accountId]: false }));
      setAiEnabled((prev) => ({ ...prev, [accountId]: false }));
      await load();
      closeAiDialog();
    } catch (err) {
      setAiError(err?.response?.data?.message || err.message || 'Ошибка удаления токена');
    } finally {
      setAiSaving(false);
    }
  };

  const getMpColor = (mp) => {
    switch (mp) {
      case 'wb':
        return { bgcolor: '#f3e5f5', color: '#6a1b9a' };
      case 'ozon':
        return { bgcolor: '#e3f2fd', color: '#1565c0' };
      case 'yandex':
        return { bgcolor: '#fff3e0', color: '#e65100' };
      case 'selsup':
        return { bgcolor: '#e8f5e9', color: '#2e7d32' };
      default:
        return { bgcolor: '#f5f5f5', color: '#616161' };
    }
  };

  const getMpLabel = (mp) => {
    switch (mp) {
      case 'wb':
        return 'Wildberries';
      case 'ozon':
        return 'Ozon';
      case 'yandex':
        return 'Yandex';
      case 'selsup':
        return 'Selsup';
      default:
        return mp;
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Аккаунты МП
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Добавить новый аккаунт
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            select
            label="Маркетплейс"
            size="small"
            value={form.marketplace}
            onChange={(e) => setForm({ ...form, marketplace: e.target.value })}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="wb">Wildberries</MenuItem>
            <MenuItem value="ozon">Ozon</MenuItem>
            <MenuItem value="yandex">Yandex.Market</MenuItem>
          </TextField>
          <TextField
            label="Название аккаунта"
            size="small"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="Токен"
            size="small"
            value={form.token}
            onChange={(e) => setForm({ ...form, token: e.target.value })}
            sx={{ minWidth: 320 }}
          />
          {form.marketplace === 'ozon' && (
            <TextField
              label="Client ID (Ozon)"
              size="small"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            />
          )}
          {form.marketplace === 'yandex' && (
            <>
              <TextField
                label="Campaign ID (YA)"
                size="small"
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              />
              <TextField
                label="Business ID (YA)"
                size="small"
                value={form.business_id}
                onChange={(e) => setForm({ ...form, business_id: e.target.value })}
              />
            </>
          )}
          <TextField
            label="MS Organization ID"
            size="small"
            value={form.ms_org_id}
            onChange={(e) => setForm({ ...form, ms_org_id: e.target.value })}
            placeholder="3cfff510-..."
          />
          <TextField
            label="Ставка НДС, %"
            size="small"
            type="number"
            value={form.nds_rate}
            onChange={(e) => setForm({ ...form, nds_rate: e.target.value })}
            inputProps={{ min: 0, max: 100, step: 1 }}
            sx={{ width: 130 }}
            helperText="0 = без НДС"
          />
          <Button variant="contained" onClick={add}>
            Добавить
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>ID</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>МП</TableCell>
              <TableCell sx={{ minWidth: 200 }}>Название</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Campaign ID</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Business ID</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>MS Org ID</TableCell>
              <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>НДС, %</TableCell>
              <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Синк заказов</TableCell>
              <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Без логистики с</TableCell>
              <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title="AI-аналитика (read-only токен)">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <SmartToyIcon fontSize="small" />
                    AI
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Активен</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover sx={!activeState[r.id] ? { opacity: 0.5 } : undefined}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.id}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Chip
                    label={getMpLabel(r.marketplace)}
                    size="small"
                    sx={getMpColor(r.marketplace)}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.name}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.client_id || '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.business_id || '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {r.ms_org_id || '—'}
                </TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  {AI_SUPPORTED_MP.has(r.marketplace) ? (
                    <TextField
                      type="number"
                      size="small"
                      value={ndsRateState[r.id] ?? '0'}
                      onChange={(e) => setNdsRateState((prev) => ({ ...prev, [r.id]: e.target.value }))}
                      onBlur={(e) => {
                        const cur = e.target.value;
                        if (String(Number(cur)) !== String(Number(r.nds_rate ?? 0))) saveNdsRate(r.id, cur);
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      inputProps={{ min: 0, max: 100, step: 1, style: { textAlign: 'center', width: 44 } }}
                      sx={{ width: 72 }}
                    />
                  ) : '—'}
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={!!syncEnabled[r.id]}
                    onChange={async (e) => {
                      const val = e.target.checked;
                      setSyncEnabled((prev) => ({ ...prev, [r.id]: val }));
                      try {
                        await axios.post('/mswb/wb/account-sync-toggle', { account_id: r.id, enabled: val });
                      } catch (_) {}
                    }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  {r.marketplace === 'wb' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TextField
                        type="date"
                        size="small"
                        value={noLogisticsFrom[r.id] || ''}
                        onChange={async (e) => {
                          const val = e.target.value;
                          setNoLogisticsFrom((prev) => ({ ...prev, [r.id]: val }));
                          try {
                            await axios.post('/mswb/wb/account-no-logistics', { account_id: r.id, date: val || null });
                          } catch (_) {}
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 160 }}
                      />
                      {noLogisticsFrom[r.id] && (
                        <IconButton
                          size="small"
                          onClick={async () => {
                            setNoLogisticsFrom((prev) => ({ ...prev, [r.id]: '' }));
                            try {
                              await axios.post('/mswb/wb/account-no-logistics', { account_id: r.id, date: null });
                            } catch (_) {}
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ) : '—'}
                </TableCell>
                <TableCell align="center">
                  {AI_SUPPORTED_MP.has(r.marketplace) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Switch
                        checked={!!aiEnabled[r.id]}
                        disabled={!hasAiToken[r.id]}
                        onChange={async (e) => {
                          const val = e.target.checked;
                          setAiEnabled((prev) => ({ ...prev, [r.id]: val }));
                          try {
                            await axios.post('/ms/accounts/toggle-ai', { account_id: r.id, enabled: val });
                          } catch (_) {}
                        }}
                        color="secondary"
                        size="small"
                      />
                      <Tooltip title={hasAiToken[r.id] ? 'Изменить AI-настройки' : 'Задать AI-токен и параметры'}>
                        <IconButton
                          size="small"
                          onClick={() => openAiDialog(r)}
                          color={hasAiToken[r.id] ? 'success' : 'default'}
                        >
                          {hasAiToken[r.id] ? <CheckCircleIcon fontSize="small" /> : <KeyIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : '—'}
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={!!activeState[r.id]}
                    onChange={async (e) => {
                      const val = e.target.checked;
                      setActiveState((prev) => ({ ...prev, [r.id]: val }));
                      try {
                        await axios.post('/ms/accounts/toggle-active', { account_id: r.id, active: val });
                      } catch (_) {}
                    }}
                    color={activeState[r.id] ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {['wb', 'ozon', 'yandex'].includes(r.marketplace) && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<WarehouseIcon fontSize="small" />}
                      onClick={() => openPickingDialog(r)}
                      sx={{ mr: 0.5 }}
                    >
                      Склады подбора
                    </Button>
                  )}
                  {MP_WITH_CRONS.has(r.marketplace) && (
                    <Tooltip title="Автозагрузка: тумблеры по cron-задачам">
                      <IconButton size="small" onClick={() => openCronDialog(r)}>
                        <TuneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Button color="error" size="small" onClick={() => del(r.id)} sx={{ ml: 1 }}>
                    Удалить
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={14} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Нет добавленных аккаунтов
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={aiDialog.open}
        onClose={aiSaving ? undefined : closeAiDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToyIcon color="secondary" />
            AI-настройки: {aiDialog.name}
            <Chip
              label={getMpLabel(aiDialog.marketplace)}
              size="small"
              sx={{ ...getMpColor(aiDialog.marketplace), ml: 1 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Read-only токен для AI-агента. Используется в инструменте marketplace_api_call,
            чтобы агент мог делать запросы к API маркетплейса от вашего имени без права на запись.
          </Typography>

          <Stack spacing={2}>
            <Box>
              <TextField
                label="AI-токен (read-only)"
                fullWidth
                type={aiShowToken ? 'text' : 'password'}
                value={aiForm.ai_token}
                onChange={(e) => setAiForm((p) => ({ ...p, ai_token: e.target.value }))}
                placeholder={aiDialog.hasToken ? '••••••••  (оставьте пустым, чтобы не менять)' : 'eyJhbGciOiJF...'}
                helperText={
                  aiDialog.hasToken
                    ? 'Токен уже установлен. Введите новое значение, чтобы заменить.'
                    : 'Создайте read-only ключ в ЛК маркетплейса и вставьте сюда.'
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setAiShowToken((s) => !s)}
                        edge="end"
                      >
                        {aiShowToken ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {aiDialog.hasToken && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Текущий токен установлен"
                  color="success"
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>

            {aiDialog.marketplace === 'ozon' && (
              <>
                <TextField
                  label="Client-Id (Ozon)"
                  fullWidth
                  value={aiForm.client_id}
                  onChange={(e) => setAiForm((p) => ({ ...p, client_id: e.target.value }))}
                  placeholder="123456"
                  helperText="Client-Id для api-seller.ozon.ru (заголовок Client-Id)"
                  required
                />

                <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Реклама (Performance API)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Используется для рекламной аналитики (cron oz_advert_stats и marketplace_api_call с
                    Performance API). Авторизация — отдельный OAuth2 client_credentials на api-performance.ozon.ru.
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Performance Client-Id"
                      fullWidth
                      value={aiForm.ozon_advert_client_id}
                      onChange={(e) => setAiForm((p) => ({ ...p, ozon_advert_client_id: e.target.value }))}
                      placeholder="30109757-1718544074388@advertising.performance.ozon.ru"
                      helperText="Client-Id для api-performance.ozon.ru (отличается от Seller Client-Id выше)"
                    />
                    <TextField
                      label="Performance Client-Secret"
                      fullWidth
                      type={aiShowOzonSecret ? 'text' : 'password'}
                      value={aiForm.ozon_advert_client_secret}
                      onChange={(e) => setAiForm((p) => ({ ...p, ozon_advert_client_secret: e.target.value }))}
                      placeholder={
                        aiDialog.hasOzonAdvertSecret
                          ? '••••••••  (оставьте пустым, чтобы не менять)'
                          : 'N9PXA9hrrjUhHOIOmNr9...'
                      }
                      helperText={
                        aiDialog.hasOzonAdvertSecret
                          ? 'Секрет уже сохранён. Введите новое значение, чтобы заменить.'
                          : 'Client-Secret для api-performance.ozon.ru (выдаётся в кабинете Ozon → Реклама → API).'
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setAiShowOzonSecret((s) => !s)}
                              edge="end"
                            >
                              {aiShowOzonSecret ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {aiDialog.hasOzonAdvertSecret && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Performance API client_secret установлен"
                        color="success"
                        variant="outlined"
                        size="small"
                        sx={{ alignSelf: 'flex-start' }}
                      />
                    )}
                  </Stack>
                </Box>
              </>
            )}

            {aiDialog.marketplace === 'yandex' && (
              <>
                <TextField
                  label="businessId (Yandex)"
                  fullWidth
                  value={aiForm.business_id}
                  onChange={(e) =>
                    setAiForm((p) => ({ ...p, business_id: e.target.value.replace(/\D/g, '') }))
                  }
                  placeholder="123456"
                  helperText="businessId для /businesses/{businessId}/... (число)"
                  required
                />
                <TextField
                  label="campaignId (Yandex)"
                  fullWidth
                  value={aiForm.client_id}
                  onChange={(e) => setAiForm((p) => ({ ...p, client_id: e.target.value }))}
                  placeholder="42424242"
                  helperText="campaignId для /campaigns/{campaignId}/... (нужен для отчётов магазина)"
                />
              </>
            )}

            {aiError && <Alert severity="error">{aiError}</Alert>}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <FormControlLabel
                control={
                  <Switch
                    color="secondary"
                    size="small"
                    checked={!!aiEnabled[aiDialog.accountId]}
                    disabled={!aiDialog.hasToken}
                    onChange={async (e) => {
                      const val = e.target.checked;
                      setAiEnabled((prev) => ({ ...prev, [aiDialog.accountId]: val }));
                      try {
                        await axios.post('/ms/accounts/toggle-ai', { account_id: aiDialog.accountId, enabled: val });
                      } catch (err) {
                        setAiEnabled((prev) => ({ ...prev, [aiDialog.accountId]: !val }));
                        setAiError(err?.response?.data?.message || 'Не удалось переключить ai_enabled');
                      }
                    }}
                  />
                }
                label="AI включён"
              />
              <Typography variant="caption" color="text.secondary">
                Сначала сохраните токен, затем включите.
              </Typography>
            </Box>

            <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Ставка НДС
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                НДС выделяется из суммы с НДС по расчётной ставке: 5 → 5/105, 7 → 7/107, 20 → 20/120. 0 — НДС не считается. Применяется при загрузке финотчётов в аналитику.
              </Typography>
              <TextField
                size="small"
                type="number"
                label="Ставка НДС, %"
                value={aiForm.nds_rate}
                onChange={(e) => setAiForm((p) => ({ ...p, nds_rate: e.target.value }))}
                inputProps={{ min: 0, max: 100, step: 1 }}
                sx={{ width: 160 }}
              />
            </Box>

            <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Уровень автономии (Этап 6)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Определяет, что AI может делать с этим аккаунтом. Глобальный kill-switch — в «Настройки → Безопасность AI».
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                label="ai_autonomy_level"
                value={aiForm.ai_autonomy_level || 'propose'}
                onChange={(e) => setAiForm((p) => ({ ...p, ai_autonomy_level: e.target.value }))}
              >
                <MenuItem value="off">off — AI выключен для аккаунта</MenuItem>
                <MenuItem value="read_only">read_only — только чтение через marketplace_api_call</MenuItem>
                <MenuItem value="propose">propose — предлагает, обязателен manual approve</MenuItem>
                <MenuItem value="auto_low">auto_low — авто только при малых изменениях (≤5% цена, ≤10% ставка)</MenuItem>
                <MenuItem value="auto_full">auto_full — авто при средних (≤15% цена, ≤25% ставка)</MenuItem>
              </TextField>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          {aiDialog.hasToken && (
            <Button color="error" onClick={deleteAiToken} disabled={aiSaving}>
              Удалить токен
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeAiDialog} disabled={aiSaving}>
            Отмена
          </Button>
          <Button variant="contained" onClick={saveAiDialog} disabled={aiSaving}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cronDialog.open}
        onClose={closeCronDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TuneIcon color="action" />
            Автозагрузка: {cronDialog.name}
            <Chip
              label={getMpLabel(cronDialog.marketplace)}
              size="small"
              sx={{ ...getMpColor(cronDialog.marketplace), ml: 1 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Для каждой автозагрузки данных из этого аккаунта можно отдельно включить или
            выключить регулярный прогон cron'а в packer_analytics. По умолчанию все включены.
          </Typography>

          {cronLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!cronLoading && cronList.length === 0 && !cronError && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Для этого маркетплейса нет настраиваемых автозагрузок.
            </Alert>
          )}

          {!cronLoading && cronList.length > 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Button size="small" onClick={() => setAllCrons(true)} disabled={cronSaving}>
                  Включить все
                </Button>
                <Button size="small" onClick={() => setAllCrons(false)} disabled={cronSaving}>
                  Выключить все
                </Button>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <Stack spacing={0.5}>
                {cronList.map((item) => (
                  <FormControlLabel
                    key={item.cron_name}
                    control={
                      <Switch
                        checked={!!item.enabled}
                        onChange={(e) => toggleCron(item.cron_name, e.target.checked)}
                        disabled={cronSaving}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{item.label}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {item.cron_name}
                        </Typography>
                      </Box>
                    }
                    sx={{ mr: 0, alignItems: 'center' }}
                  />
                ))}
              </Stack>
            </>
          )}

          {cronError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cronError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeCronDialog} disabled={cronSaving}>
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={saveCronDialog}
            disabled={cronSaving || cronLoading || cronList.length === 0}
          >
            {cronSaving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      <PickingMsSettingsDialog
        open={pickingDialog.open}
        accountId={pickingDialog.accountId}
        accountName={pickingDialog.name}
        marketplace={pickingDialog.marketplace}
        onClose={closePickingDialog}
      />
    </Box>
  );
}
